import PageLoader from '@/components/PageLoader';
import {
  AUTH_LOCAL_STORAGE,
  AUTHORIZED_ROUTES_LOCAL_STORAGE,
  PERMISSION_LOCAL_STORAGE,
  RESOURCE_LOCAL_STORAGE,
  TOKEN_COOKIE_STORAGE,
} from '@/constants/localStorageKeyConstants';
import { selectAuth } from '@/redux/auth/selectors';
import cryptoHelper from '@/utils/crypto';
import CookieManager from '@/utils/helpers/cookieUtils';
import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { routesConfig } from './RoutesConfig';

const Logout = lazy(() => import('@/pages/Logout'));
const UnAuthorized = lazy(() => import('@/pages/UnAuthorized'));

function isRouteAuthorized(userRole, routePath, storedAuthorizedRoutes) {
  if (userRole in storedAuthorizedRoutes) {
    const allowedRoutes = storedAuthorizedRoutes[userRole];

    return allowedRoutes.includes(routePath);
  }

  return false;
}

export default function AppRouter() {
  const location = useLocation();
  const currentUser = useSelector(selectAuth);
  const { role } = currentUser;
  const currentUserRole = role?.name;
  const key = process.env.COOKIE_SECRET_KEY;
  const authorizedRoutesFromStorage = localStorage.getItem(AUTHORIZED_ROUTES_LOCAL_STORAGE);
  const PERMISSIONS = localStorage.getItem(PERMISSION_LOCAL_STORAGE);
  const RESOURCES = localStorage.getItem(RESOURCE_LOCAL_STORAGE);
  const AUTH = localStorage.getItem(AUTH_LOCAL_STORAGE);
  const storedAuthorizedRoutes = authorizedRoutesFromStorage
    ? JSON.parse(cryptoHelper.decrypt(authorizedRoutesFromStorage, key))
    : null;
  if (!storedAuthorizedRoutes || !AUTH || !PERMISSIONS || !RESOURCES) {
    window.localStorage.removeItem(AUTH_LOCAL_STORAGE);
    window.localStorage.removeItem(PERMISSION_LOCAL_STORAGE);
    window.localStorage.removeItem(RESOURCE_LOCAL_STORAGE);
    window.localStorage.removeItem(AUTHORIZED_ROUTES_LOCAL_STORAGE);
    CookieManager.clearCookie(TOKEN_COOKIE_STORAGE);
    window.location.href = '/login';
    return <PageLoader></PageLoader>;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence exitBeforeEnter initial={false}>
        <Switch location={location} key={location.pathname}>
          {routesConfig.map((routeItem) => {
            if (isRouteAuthorized(currentUserRole, routeItem.path, storedAuthorizedRoutes)) {
              return (
                <PrivateRoute
                  key={routeItem.component}
                  path={routeItem.path}
                  exact={routeItem.exact || true}
                  component={lazy(() => import(`@/pages/${routeItem.component}`))}
                />
              );
            }
          })}

          <PublicRoute path="/login" render={() => <Redirect to="/" />} exact />
          <Route component={Logout} path="/logout" exact />
          <Route path="*" component={UnAuthorized} render={() => <Redirect to="/unauthorized" />} />
        </Switch>
      </AnimatePresence>
    </Suspense>
  );
}
